import { createSlice } from "@reduxjs/toolkit";
import {
  getPhotoLibrary,
  getExtraItems,
  isExtraItemChecked,
  getMealDetailById,
  addNewExtraItem,
  addNewImages,
  addOrUpdateMealDetail,
  getAllMeals,
  onDeleteMeal,
  onMealStatusUpdate,
  onDeleteExtraItems,
} from "../thunks/mealsetupThunk";
import { handleRegistrationStatus } from "../../utils/registrationHelper";

const initialState = {
  isMealLoading: false,
  isExtraLoading: false,
  mealErr: null,
  mealGalleries: [],
  getExtra: [],
  addItem: null,
  otherUploadedImages: [],
  allMeals: [],
  currentPage: 1,
  totalPages: 1,
  totalRecords: 0,
  mealPlanAmt: 0,
};

const mealsetUpSlice = createSlice({
  name: "setMeal",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ===== PHOTO LIBRARY =====
      .addCase(getPhotoLibrary.pending, (state) => {
        state.isMealLoading = true;
        state.mealErr = null;
      })
      .addCase(getPhotoLibrary.fulfilled, (state, action) => {
        state.isMealLoading = false;
        // store only the gallery array
        state.mealGalleries = action.payload.data || [];
      })
      .addCase(getPhotoLibrary.rejected, (state, action) => {
        state.isMealLoading = false;
        state.mealErr =
          action.payload?.message ||
          action.error?.message ||
          "Failed to load gallery";
      })

      // ===== EXTRA ITEMS =====
      .addCase(getExtraItems.pending, (state) => {
        state.isExtraLoading = true;
      })
      .addCase(getExtraItems.fulfilled, (state, action) => {
        state.isExtraLoading = false;

        const data = action.payload.data;

        state.getExtra = data?.extra_items || [];
        state.currentPage = data?.current_page || 1;
        state.totalPages = data?.total_pages || 1;
      })
      .addCase(getExtraItems.rejected, (state, action) => {
        state.isExtraLoading = false;
        state.mealErr =
          action.payload?.message ||
          action.error?.message ||
          "Failed to load extra items";
      })

      // ===== EXTRA ITEM CHECKED STATUS =====
      .addCase(isExtraItemChecked.pending, (state) => {
        state.isExtraLoading = true;
      })
      .addCase(isExtraItemChecked.fulfilled, (state, action) => {
        state.isExtraLoading = false;
        const { extra_item_id, new_status } = action.payload.data;
        const found = state.getExtra.find((item) => item.id === extra_item_id);
        if (found) found.status = new_status;
      })
      .addCase(isExtraItemChecked.rejected, (state, action) => {
        state.isExtraLoading = false;
        state.mealErr =
          action.payload?.message ||
          action.error?.message ||
          "Failed to update extra item";
      })

      // ===== ADD NEW EXTRA ITEM =====
      .addCase(addNewExtraItem.pending, (state) => {
        state.isExtraLoading = true;
      })
      .addCase(addNewExtraItem.fulfilled, (state, action) => {
        state.isExtraLoading = false;
        // store the new item itself
        state.addItem = action.payload.data || null;
      })
      .addCase(addNewExtraItem.rejected, (state, action) => {
        state.isExtraLoading = false;
        state.mealErr =
          action.payload?.message ||
          action.error?.message ||
          "Failed to add extra item";
      })

      // ===== UPLOAD IMAGES =====
      .addCase(addNewImages.pending, (state) => {
        state.isMealLoading = true;
      })
      .addCase(addNewImages.fulfilled, (state, action) => {
        state.isMealLoading = false;
        // store only uploaded images array
        state.otherUploadedImages = action.payload.data?.temp_images || [];
      })
      .addCase(addNewImages.rejected, (state, action) => {
        state.isMealLoading = false;
        state.mealErr =
          action.payload?.message ||
          action.error?.message ||
          "Failed to upload images";
      })

      // ===== ADD OR UPDATE MEAL =====
      .addCase(addOrUpdateMealDetail.pending, (state) => {
        state.isMealLoading = true;
      })
      .addCase(addOrUpdateMealDetail.fulfilled, (state, action) => {
        state.isMealLoading = false;
        // store only the message or updated meal object if needed
        state.addItem = action.payload.data || null;
        handleRegistrationStatus(action.payload);
      })
      .addCase(addOrUpdateMealDetail.rejected, (state, action) => {
        state.isMealLoading = false;
        state.mealErr =
          action.payload?.message ||
          action.error?.message ||
          "Failed to save meal";
      })

      // ===== GET ALL MEALS =====
      .addCase(getAllMeals.pending, (state) => {
        state.isMealLoading = true;
      })
      .addCase(getAllMeals.fulfilled, (state, action) => {
        state.isMealLoading = false;
        const mealsData = action.payload.data || {};
        state.allMeals = mealsData.meals || [];
        state.currentPage = mealsData.current_page || 1;
        state.totalPages = mealsData.total_pages || 1;
        state.totalRecords = mealsData.total_records || 0;
      })
      .addCase(getAllMeals.rejected, (state, action) => {
        state.isMealLoading = false;
        state.mealErr =
          action.payload?.message ||
          action.error?.message ||
          "Failed to fetch meals";
      })

      // On deleting the meal
      .addCase(onDeleteMeal.pending, (state) => {
        state.isMealLoading = true;
      })
      .addCase(onDeleteMeal.fulfilled, (state, action) => {
        state.isMealLoading = false;
        state.action = action.payload;
      })
      .addCase(onDeleteMeal.rejected, (state) => {
        state.isMealLoading = false;
      })

      // On Status Update
      .addCase(onMealStatusUpdate.pending, (state) => {
        state.isMealLoading = true;
      })
      .addCase(onMealStatusUpdate.fulfilled, (state, action) => {
        state.isMealLoading = false;
        state.action = action.payload;
      })
      .addCase(onMealStatusUpdate.rejected, (state) => {
        state.isMealLoading = false;
      })

      // On delete extra items
      .addCase(onDeleteExtraItems.pending, (state) => {
        state.isExtraLoading = true;
      })
      .addCase(onDeleteExtraItems.fulfilled, (state, action) => {
        state.isExtraLoading = false;
        state.action = action.payload;
      })
      .addCase(onDeleteExtraItems.rejected, (state) => {
        state.isExtraLoading = false;
      })

      // get Meal details by id
      .addCase(getMealDetailById.pending, (state) => {
        state.isMealLoading = true;
      })
      .addCase(getMealDetailById.fulfilled, (state, action) => {
        ((state.isMealLoading = false), (state.action = action.payload));
      })
      .addCase(getMealDetailById.rejected, (state) => {
        state.isMealLoading = false;
      });
  },
});

export default mealsetUpSlice.reducer;
